import React, { useEffect, useMemo, useRef } from 'react';
import P from 'prop-types';
import { REGISTRATION_EMBEDS } from '@nerdwallet/js-auth-tools/registration';
import analytics from '@nerdwallet/analytics';
import { group, stringInputSpec } from '../../lib/editing-configs';
import UniversalContainer, {
  universalContainerPropTypes,
  universalContainerDefaultProps,
} from '../universal-container/universal-container';

const UniversalAuthButton = ({
  isSignedIn,
  componentName,
  loginTrkLocation,
  loginTrkFormat,
  ...container
}) => {
  const signUpRef = useRef(null);
  const analyticsValues = useMemo(() => {
    return {
      trk: 'lpc',
      element_name: componentName,
      section: componentName,
      trk_location: loginTrkLocation,
      trk_format: loginTrkFormat,
    };
  }, [componentName, loginTrkFormat, loginTrkLocation]);

  useEffect(() => {
    if (!signUpRef.current || isSignedIn) {
      return;
    }

    const redirectToDashboard = () => {
      if (window) {
        window.location = '/home/dashboard/home';
      }
    };

    const openRegisterAuthDialogHelper = () => {
      window?.nwapi?.ui?.showAuthDialog({
        authType: 'register',
        version: 2,
        trk: {
          ...analyticsValues,
        },
        flowConfig: {
          errorToastPositionMobile: 'bottom-center',
        },
        onSuccessActions: redirectToDashboard,
      });
    };

    window.nwapi?.ui?.showAuthEmbed({
      el: signUpRef.current,
      authType: 'register',
      flowConfig: {
        enableSuccessErrorModals: false,
        openRegisterAuthDialog: openRegisterAuthDialogHelper,
      },
      regFlow: REGISTRATION_EMBEDS.AWARDS_PAGE_EMBED,
      version: 2,
      flow: 'no-security-question',
      trk: analyticsValues.trk,
      regTrackingAttributes: {
        entityNames: {
          authButtons: 'auth_button',
        },
        sectionName: analyticsValues.section,
      },
      onSuccess() {
        analytics.track('element_impression', {
          entity_name: 'success_modal',
          section_name: analyticsValues.section,
        });
        redirectToDashboard();
      },
      onFailure() {
        analytics.track('element_impression', {
          entityName: 'error_modal',
          sectionName: analyticsValues.section,
        });
      },
    });
  }, [
    analyticsValues,
    analyticsValues.section,
    analyticsValues.trk,
    isSignedIn,
    loginTrkFormat,
    loginTrkLocation,
  ]);

  return (
    <UniversalContainer {...container}>
      <div ref={signUpRef} />
    </UniversalContainer>
  );
};

UniversalAuthButton.displayName = 'UniversalAuthButton';

UniversalAuthButton.propTypes = {
  isSignedIn: P.bool,
  componentName: P.string,
  loginTrkLocation: P.string,
  loginTrkFormat: P.string,
  ...universalContainerPropTypes,
};

UniversalAuthButton.defaultProps = {
  isSignedIn: false,
  componentName: 'auth-button',
  loginTrkLocation: '',
  loginTrkFormat: '',
  ...universalContainerDefaultProps,
};

UniversalAuthButton.getEditingConfig = (
  componentDocument,
  fieldName = 'universal-auth-button'
) => {
  return [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Button Container',
      ['backgroundColor', 'padding', 'margin'],
      fieldName
    ),
    group('Tracking', [
      stringInputSpec(`${fieldName}.componentName`, 'Component Name', {
        help: 'For Tracking Purposes',
      }),
      stringInputSpec(`${fieldName}.loginTrkLocation`, 'trk_location value', {
        help: 'Enter a value for trk_location for authentication and sign-up.',
      }),
      stringInputSpec(`${fieldName}.loginTrkFormat`, 'trk_format value', {
        help: 'Enter a value for trk_format for authentication and sign-up.',
      }),
    ]),
  ];
};

export default UniversalAuthButton;
